import queryString from "query-string";

const { parse, stringify } = queryString;

// CREW-1169 — the vacancies filter lives in the URL, the same contract the task
// manager uses (CREW-1040/A2):
//   URL --getFilterByLocationSearch--> filter (on LOCATION_CHANGE)
//   filter --getQueryByFilter--> URL         (on APPLY_FILTER)
//
// One difference decides the shape here. Task-manager filters are flat strings;
// these are react-select values — `{value, label}` objects, multi-select for
// three of the four lists, and vessels/managers/owners load asynchronously.
// The URL therefore carries **only the ids**: that is what the backend filters
// on, and it keeps a shared link short and free of arbitrary label text. A
// parsed option comes back with `label: undefined`, which the chips render as
// "N selected" until the option lists arrive with the real names.

export const MULTI_KEYS = ["vessel", "role", "manager", "owner"];
// CREW-1230 — `archived` is a boolean the same way `published` is, so parsing
// and serialising it into the URL comes for free from this list. Absent or
// `false` both mean "open vacancies only", which is the default view.
export const BOOLEAN_KEYS = ["published", "archived"];
// A flag is on or absent — never "off". `assigned_to_me` is the backend's
// "my vacancies" filter (CREW-1167) and has no negative form: there is no
// query for "vacancies somebody else took". Writing `assigned_to_me=false`
// into the URL would look meaningful and filter nothing.
// CREW-1233 — `overdue` joins it: a vacancy whose joining date has passed is
// either what you asked for or not part of the question. "Not overdue" is the
// default list, so a negative form would look meaningful and filter nothing.
export const FLAG_KEYS = ["assigned_to_me", "overdue"];
// The free-text search box. A filter like any other, and it belongs in the
// address bar with them: what the list shows must be what the URL says. It used
// to be seeded from localStorage, so opening a colleague's link silently
// re-applied whatever you last typed into your own box — the link meant
// something different for every person.
export const SEARCH_KEY = "search";

export const FILTER_KEYS = [SEARCH_KEY, ...MULTI_KEYS, ...BOOLEAN_KEYS, ...FLAG_KEYS];

// CREW-1209 — which filter keys can have their names fetched by id, and the
// backend type that answers for each. Only the page knows its own keys.
//
// `role` is deliberately absent: the rank list is in the store from the first
// render (`optionsOfRoles`), so its chips are named without asking anyone.
// Booleans and flags carry no ids at all.
export const RESOLVABLE_TYPES = {
  vessel: "vessels",
  manager: "managers",
  owner: "owners",
};

const isBlank = (value) =>
  value === undefined || value === null || `${value}`.length === 0;

/**
 * getFilterByLocationSearch
 * Parse a location.search string into the vacancies filter object.
 * Multi keys become arrays of `{value}`; `published` becomes a single
 * `{value: boolean}`. Labels are deliberately absent — see the note above.
 * @param {String} search location.search ("?a=b" or "a=b")
 * @returns {Object} filter
 */
export const getFilterByLocationSearch = (search) => {
  const params = parse(search || "", { arrayFormat: "comma" });

  return FILTER_KEYS.reduce((filter, key) => {
    const raw = params[key];
    if (isBlank(raw)) return filter;

    if (key === SEARCH_KEY) {
      filter[key] = `${raw}`.trim();
      return filter;
    }

    if (FLAG_KEYS.includes(key)) {
      if (`${raw}` !== "true") return filter;
      filter[key] = { value: true };
      return filter;
    }

    if (BOOLEAN_KEYS.includes(key)) {
      // Only the two values the dropdown can produce; anything else is junk
      // in a hand-edited URL and is dropped rather than sent to the backend.
      if (`${raw}` !== "true" && `${raw}` !== "false") return filter;
      filter[key] = { value: `${raw}` === "true" };
      return filter;
    }

    const values = Array.isArray(raw) ? raw : [raw];
    const options = values.filter((value) => !isBlank(value)).map((value) => ({ value: `${value}` }));
    if (options.length) filter[key] = options;

    return filter;
  }, {});
};

/**
 * withLabelsFrom
 * Carry `label` across from a filter we already hold onto one just parsed out
 * of the URL, matching on `value`.
 *
 * CREW-1208. Apply writes ids into the URL and its own LOCATION_CHANGE parses
 * them straight back — labelless, because the URL carries ids only. Letting
 * that overwrite the store left the drawer rendering chips with nothing but a
 * cross. The labels are still in `appliedFilter` at that moment, so they are
 * simply carried over; nothing is fetched and the URL contract is untouched.
 *
 * A value the previous filter never held keeps no label — that is the cold
 * entry (a shared link, a reload), and resolving those needs the backend
 * (CREW-1209).
 *
 * @param {Object} filter - freshly parsed from the URL
 * @param {Object} previous - the filter already in the store
 * @returns {Object}
 */
export const withLabelsFrom = (filter = {}, previous = {}) =>
  Object.entries(filter).reduce((acc, [key, value]) => {
    // Booleans and flags are single values, not option lists.
    if (!MULTI_KEYS.includes(key) || !Array.isArray(value)) {
      acc[key] = value;
      return acc;
    }

    const labels = new Map(
      ((previous || {})[key] || []).map((option) => [`${option.value}`, option.label]),
    );

    acc[key] = value.map((option) => {
      const label = labels.get(`${option.value}`);
      return label === undefined ? option : { ...option, label };
    });

    return acc;
  }, {});

/**
 * getQueryByFilter
 * Serialize the vacancies filter into a stable (alphabetically sorted) query
 * string carrying ids only. Empty selections are omitted, so "no filters" is a
 * clean /vacancies URL.
 * @param {Object} filter
 * @returns {String} query string without the leading "?"
 */
export const getQueryByFilter = (filter = {}) => {
  const query = FILTER_KEYS.reduce((acc, key) => {
    const value = filter[key];
    if (value === undefined || value === null) return acc;

    if (key === SEARCH_KEY) {
      const text = `${value}`.trim();
      if (text) acc[key] = text;
      return acc;
    }

    if (FLAG_KEYS.includes(key)) {
      const single = Array.isArray(value) ? value[0] : value;
      const on = single && typeof single === "object" ? single.value : single;
      if (on === true || `${on}` === "true") acc[key] = "true";
      return acc;
    }

    if (BOOLEAN_KEYS.includes(key)) {
      const single = Array.isArray(value) ? value[0] : value;
      if (!single || isBlank(single.value)) return acc;
      acc[key] = `${single.value}`;
      return acc;
    }

    const options = Array.isArray(value) ? value : [value];
    // Sorted, so the same selection always spells the same URL no matter what
    // order the user ticked the boxes in. That makes a link stable, and lets
    // "is this filter the preset / the URL we are already on?" be a string
    // comparison instead of a set comparison.
    const ids = options
      .map((option) => (option && typeof option === "object" ? option.value : option))
      .filter((id) => !isBlank(id))
      .map((id) => `${id}`)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    if (ids.length) acc[key] = ids;

    return acc;
  }, {});

  return stringify(query, {
    arrayFormat: "comma",
    sort: (a, b) => a.localeCompare(b),
  });
};
